import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { OrganizationWithWorkspaces } from '@/types/Organization';
import { Button, DropdownItem, Subtitle } from '@tremor/react';
import { filter, find, map } from 'lodash';
import { doc, DocumentData, DocumentSnapshot, onSnapshot } from 'firebase/firestore';
import { db, restartOrganizationCloudFunction } from '@/firebase/init';
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator';
import {
  useLocation,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from 'react-router-dom';
import { GeneralModalProps } from '@/types/modals';
import { ModalWrapper } from '../ModalWrapper/ModalWrapper';
import { useOrganizationStore } from '@/stores/organizationStore';
import { OutlinedSelectInput } from '../../Inputs/OutlinedSelectInput/OutlinedSelectInput';
import { ModalNavLink } from './ModalNavLink/ModalNavLink';
import { FlatAddButton } from '../../Buttons/FlatAddButton/FlatAddButton';
import { OutlinedButton } from '../../Buttons/OutlinedButton/OutlinedButton';
import { NAV_ROUTES } from '@/utils/constants';
import { SearchBar } from '../../Inputs/SearchBar/SearchBar';
import { canCreateOrganizations } from '@/utils/helpers';

interface CommonProps extends GeneralModalProps, PropsWithChildren {
  handleOpenNewWorkspace: () => void;
  setExternalLocalSelectedOrganization: Dispatch<
    SetStateAction<OrganizationWithWorkspaces | null>
  >;
  externalLocalSelectedOrganization: OrganizationWithWorkspaces | null;
}

type Props = CommonProps;

export const SwitchEntityModal: FC<Props> = ({ isOpen, setClose, ...rest }) => {
  return (
    <ModalWrapper isOpen={isOpen} onRequestClose={setClose}>
      <SwitchEntityModalContent key={String(isOpen)} setClose={setClose} {...rest} />
    </ModalWrapper>
  );
};

const SwitchEntityModalContent: FC<Omit<Props, 'isOpen' | 'children'>> = ({
  setClose,
  handleOpenNewWorkspace,
  setExternalLocalSelectedOrganization,
  externalLocalSelectedOrganization,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const location = useLocation();
  const [newOrganizationId, setNewOrganizationId] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [localSelectedOrganization, setLocalSelectedOrganization] =
    useState<OrganizationWithWorkspaces | null>(externalLocalSelectedOrganization);
  const [value, setValue] = useState(localSelectedOrganization?.id);
  const [searchPhrase, setSearchPhrase] = useState('');

  useEffect(() => {
    if (searchParams.has('newlyCreatedOrganizationId')) {
      const params = new URLSearchParams(searchParams);
      params.delete('newlyCreatedOrganizationId');
      setSearchParams(params);
    }
  }, [searchParams, setSearchParams]);

  const setSelectedOrganization = useOrganizationStore(
    (state) => state.setSelectedOrganization,
  );
  const organizations = useOrganizationStore((state) => state.organizations);
  const selectOrganizations = map(
    filter(organizations, ({ annotation }) => !annotation),
    ({ id, name }) => ({
      id,
      name,
    }),
  );
  if (canCreateOrganizations()) {
    selectOrganizations.push({ id: 'create-new', name: 'Create New' });
  }

  const cannotAddWorkspace =
    localSelectedOrganization === null ||
    localSelectedOrganization.status !== 'TENANT_SUCCESS';
  const workspaces = useMemo(() => {
    return filter(localSelectedOrganization?.workspaces || [], (workspace) =>
      workspace.name.toLowerCase().includes(searchPhrase.toLowerCase()),
    );
  }, [localSelectedOrganization?.workspaces, searchPhrase]);

  useEffect(() => {
    if (!newOrganizationId) return;
    const retryOrganization = find(organizations, { id: newOrganizationId });
    const isOrganizationInSelectedOrganization = find(selectOrganizations, {
      id: newOrganizationId,
    });
    if (
      localSelectedOrganization?.annotation &&
      retryOrganization &&
      isOrganizationInSelectedOrganization
    ) {
      if (retryOrganization) {
        setLocalSelectedOrganization(retryOrganization);
        setValue(retryOrganization.id);
      }
    }
  }, [
    localSelectedOrganization?.annotation,
    newOrganizationId,
    organizations,
    selectOrganizations,
  ]);

  const handleSnapshotUpdate = useCallback(
    (doc: DocumentSnapshot<DocumentData>) => {
      const selectedOrganization = {
        ...find(organizations, {
          id: doc.id,
        }),
      } as OrganizationWithWorkspaces;
      const data = doc.data();
      selectedOrganization.status = data?.status;
      setLocalSelectedOrganization(selectedOrganization);
      const isStaleData =
        data?.status === 'TENANT_SUCCESS' &&
        localSelectedOrganization &&
        !localSelectedOrganization.permissions;
      if (isStaleData) {
        revalidator.revalidate();
      }
    },
    [localSelectedOrganization, organizations, revalidator],
  );

  useEffect(() => {
    if (
      !localSelectedOrganization?.id ||
      !localSelectedOrganization?.isOrganizationMember
    )
      return;
    const unsub = onSnapshot(
      doc(db, `organizations`, localSelectedOrganization?.id),
      handleSnapshotUpdate,
    );
    return unsub;
  }, [
    localSelectedOrganization?.id,
    localSelectedOrganization?.isOrganizationMember,
    handleSnapshotUpdate,
  ]);

  const isStatusFailure = localSelectedOrganization?.status?.includes('FAILURE');

  const onSelect = (val: string) => {
    if (val === 'create-new') {
      navigate(`${NAV_ROUTES.subscriptions}?${searchParams.toString()}`);
      return setClose();
    }
    const selectedOrganization = find(organizations, {
      id: val,
    }) as OrganizationWithWorkspaces;
    setLocalSelectedOrganization(selectedOrganization);
    setExternalLocalSelectedOrganization(selectedOrganization);
    setValue(selectedOrganization.id);
  };

  const onSelectWorkspace = (organization: OrganizationWithWorkspaces) => {
    return function (workspaceId: string) {
      setSelectedOrganization(organization);
      setExternalLocalSelectedOrganization(organization);
      const pathsToResetUrl = [
        NAV_ROUTES.subscriptions,
        NAV_ROUTES.handleInvitation,
        NAV_ROUTES.missingPermissions,
        NAV_ROUTES.wrongSubscriptionTier,
        NAV_ROUTES.verifyEmail,
      ];
      setClose();
      if (
        pathsToResetUrl.includes(location.pathname as (typeof pathsToResetUrl)[number])
      ) {
        return navigate(
          { search: `workspaceId=${workspaceId}`, hash: '', pathname: NAV_ROUTES.root },
          { relative: 'route' },
        );
      }
      navigate({ search: `workspaceId=${workspaceId}`, hash: '' }, { relative: 'route' });
    };
  };

  return (
    <>
      <section className="flex flex-row justify-between items-center p-0 w-[700px] h-[40px]">
        <h3 className="h-9 font-ubuntu font-[700] text-2xl flex items-center text-[425565]">
          Switch Workspace
        </h3>
        <h4 className="h-9 font-ubuntu font-[700] text-base ml-auto flex mr-4 items-center text-[425565]">
          Select Organization:
        </h4>

        <OutlinedSelectInput
          placeholder="Select Organization"
          onChange={onSelect}
          value={value}
          className="mr-0 w-52 justify-between"
        >
          {selectOrganizations.map((organization) => {
            return (
              <DropdownItem
                key={organization.id}
                value={organization.id}
                text={organization.name}
              />
            );
          })}
        </OutlinedSelectInput>
      </section>
      {localSelectedOrganization?.status === 'TENANT_SUCCESS' && (
        <>
          <SearchBar
            placeholder="Filter Workspaces"
            onSubmit={setSearchPhrase}
            cancelButton
          />
          <ul className="flex flex-col items-start p-0 gap-2 w-full overflow-y-auto scrollbar-thumb-only">
            {workspaces.map((workspace) => (
              <ModalNavLink
                key={workspace.id}
                workspace={workspace}
                onSelectWorkspace={onSelectWorkspace(localSelectedOrganization)}
              />
            ))}
          </ul>
        </>
      )}
      <FlatAddButton disabled={cannotAddWorkspace} onClick={handleOpenNewWorkspace}>
        create workspace
      </FlatAddButton>
      {localSelectedOrganization?.status !== 'TENANT_SUCCESS' &&
        localSelectedOrganization &&
        !isStatusFailure && (
          <LoadingIndicator
            isDeleting={
              localSelectedOrganization.status === 'AWAITING_DELETION' &&
              !localSelectedOrganization.annotation
            }
          />
        )}
      {isStatusFailure && (
        <section className="flex flex-col items-center justify-center gap-5 p-5 w-[700px] h-[300px]">
          <Subtitle>
            We are sorry, but we are unable to prepare Your organization. Please retry
            using this button or contact support
          </Subtitle>
          <OutlinedButton
            disabled={isResetting}
            onClick={async () => {
              setIsResetting(true);
              try {
                if (!localSelectedOrganization) return;
                const newOrganizationId = await restartOrganizationCloudFunction({
                  organizationId: localSelectedOrganization?.id,
                });
                revalidator.revalidate();
                setNewOrganizationId(newOrganizationId.data);
              } finally {
                setIsResetting(false);
              }
            }}
          >
            Retry
          </OutlinedButton>
        </section>
      )}
      <Button
        onClick={setClose}
        className="flex justify-center py-1.5 px-4 items-center border-2 shadow-none text-primary-700 bg-transparent mt-auto border-primary-700 outline-none rounded-xl [&_p]:text-xs hover:bg-primary-700 hover:text-white transition-colors duration-200 w-1/5 h-9 font-bold"
      >
        Close
      </Button>
    </>
  );
};
